/**
 * Assignment Detail Page
 * Shows assignment details and allows making/accepting offers
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../core/contexts/AuthContext';
import useAssignments from '../hooks/useAssignments';
import { getUserProfile } from '../../../services/firebase/profileService'; // Fixed import
import AssignmentDetailComponent from '../components/AssignmentDetail';
import OfferForm from '../components/OfferForm';
import OfferCard from '../components/OfferCard';
import { MessageSquare, AlertTriangle } from 'lucide-react';

const AssignmentDetail = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { 
    singleAssignment, 
    assignmentOffers, 
    loading, 
    offerLoading, 
    loadAssignmentById, 
    loadAssignmentOffers, 
    createOffer, 
    acceptOffer, 
    completeAssignment 
  } = useAssignments();
  
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Load assignment data on component mount
  useEffect(() => {
    if (currentUser && assignmentId) {
      loadAssignmentById(assignmentId);
      loadAssignmentOffers(assignmentId);
    }
  }, [currentUser, assignmentId]);
  
  // Fetch assignment owner profile
  useEffect(() => {
    const fetchOwnerProfile = async () => {
      if (singleAssignment && singleAssignment.userId) {
        try {
          const profile = await getUserProfile(singleAssignment.userId);
          setOwnerProfile(profile);
        } catch (error) {
          console.error('Error fetching owner profile:', error);
        }
      }
    };
    
    fetchOwnerProfile();
  }, [singleAssignment]);
  
  // Check if current user is the assignment owner
  const isOwner = singleAssignment && currentUser && singleAssignment.userId === currentUser.uid;
  
  // Check if current user has already made an offer
  const hasUserOffered = assignmentOffers && assignmentOffers.some(
    offer => offer.tutorId === currentUser?.uid
  );
  
  // Handle offer submission
  const handleSubmitOffer = async (offerData) => {
    if (!currentUser || !assignmentId) return;
    
    const offerId = await createOffer(assignmentId, offerData);
    if (offerId) {
      // Reload offers after submission
      await loadAssignmentOffers(assignmentId);
    }
  };
  
  // Handle accepting an offer
  const handleAcceptOffer = async (offerId) => {
    if (!currentUser || !assignmentId || !isOwner) return;
    
    setActionLoading(true);
    const success = await acceptOffer(assignmentId, offerId);
    setActionLoading(false);
    
    if (success) {
      // Reload assignment and offers
      await loadAssignmentById(assignmentId);
      await loadAssignmentOffers(assignmentId);
    }
  };
  
  // Handle completing an assignment
  const handleCompleteAssignment = async () => {
    if (!currentUser || !assignmentId || !isOwner) return;
    
    setActionLoading(true);
    const success = await completeAssignment(assignmentId);
    setActionLoading(false);
    
    if (success) {
      // Reload assignment
      await loadAssignmentById(assignmentId);
    }
  };
  
  if (loading && !singleAssignment) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex justify-center items-center py-16">
          <div className="w-6 h-6 border-2 border-t-transparent border-green-500 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600">Loading assignment details...</span>
        </div>
      </div>
    );
  }
  
  if (!singleAssignment) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Assignment Not Found</h2>
          <p className="text-red-700 mb-4">
            The assignment you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/assignments')}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
          >
            Back to Assignment Hub
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Assignment Details */}
      <AssignmentDetailComponent 
        assignment={singleAssignment}
        isOwner={isOwner}
        onComplete={handleCompleteAssignment}
        isLoading={actionLoading}
        ownerProfile={ownerProfile}
      />
      
      {/* Offers Section - Only for Open Assignments */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <MessageSquare className="mr-2 text-green-500" size={22} />
          {isOwner ? 'Tutor Offers' : 'Make an Offer'}
        </h2>
        
        {/* Different content based on role and assignment status */}
        {singleAssignment.status === 'open' && !isOwner && (
          <>
            {hasUserOffered ? (
              <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-6">
                <p className="text-green-800 font-medium">
                  You have already submitted an offer for this assignment.
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <OfferForm 
                  assignment={singleAssignment}
                  onSubmit={handleSubmitOffer}
                  isLoading={offerLoading}
                />
              </div>
            )}
          </>
        )}
        
        {singleAssignment.status !== 'open' && !isOwner && (
          <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 font-medium flex items-center">
              <AlertTriangle size={18} className="mr-2" />
              This assignment is no longer accepting offers.
            </p>
          </div>
        )}
        
        {/* Offers List - Visible to assignment owner or if you've made an offer */}
        {(isOwner || hasUserOffered) && (
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-800">
              {isOwner ? 'Offers' : 'Your Offer'}
              {assignmentOffers && (
                <span className="text-sm text-gray-500 font-normal ml-2">
                  ({assignmentOffers.length})
                </span>
              )}
            </h3>
            
            {offerLoading && !assignmentOffers ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-t-transparent border-green-500 rounded-full animate-spin"></div>
                <span className="ml-2 text-gray-600">Loading offers...</span>
              </div>
            ) : assignmentOffers && assignmentOffers.length > 0 ? (
              <div className="space-y-4">
                {assignmentOffers.map(offer => (
                  <OfferCard 
                    key={offer.id}
                    offer={offer}
                    isOwner={isOwner}
                    onAccept={() => handleAcceptOffer(offer.id)}
                    isLoading={actionLoading}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 text-center py-8 rounded-lg">
                <p className="text-gray-500">
                  {isOwner 
                    ? 'No offers yet. Your assignment is waiting for tutors to respond.'
                    : 'Your offer is being processed.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentDetail;